import React, { useEffect, useMemo, useState } from 'react';
import styled, { css, keyframes } from 'styled-components';
import tw from 'twin.macro';
import { useMutation, useQuery } from '@apollo/client';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { GET_CAR, UPDATE_CAR } from '../../../../services/carService/queries';
import { GET_PROTECTION_PLANS } from '../../../../services/protectionPlanService/queries';
import { GET_TRIP_FEES } from '../../../../services/tripFeeServices/queries';
import { authStore } from '../../../../../store/authStore';
import {
  ITripFee,
  ProtectionPlanEnum,
  TripFeeTypeEnums,
  TripFeeUniqNameEnums,
} from '../../../../../__generated__/graphql';
import { Button } from '../../../../components/button';
import NumericInput from '../../../../components/NumericInput';
import { useBrandLocale } from '../../../../hooks/useBrandLocale';
import { defaultDailyDistance, defaultMonthlyDistance } from '../../../../utils/units';

const Container = styled.div`
  ${tw`p-6 max-w-4xl`}
`;

const Title = styled.h1`
  ${tw`text-2xl font-bold mb-6`}
`;

const SectionTitle = styled.h2`
  ${tw`text-lg font-semibold mb-3 text-text-primary`}
`;

const Grid = styled.div`
  ${tw`grid grid-cols-1 md:grid-cols-3 gap-4 mb-8`}
`;

const Field = styled.div`
  ${tw`flex flex-col gap-1`}
`;

const Label = styled.label`
  ${tw`text-sm font-medium text-text-secondary mb-1 block`}
`;

const PriceInput = styled(NumericInput)`
  ${tw`p-2 border rounded-md w-full`}
`;

type DistanceLimit = {
  dailyDistanceLimit?: number | null;
  unlimitedMileage?: boolean | null;
  monthlyDistanceLimit?: number | null;
  monthlyUnlimitedMileage?: boolean | null;
};

const EligibilityWarning = styled.div`
  ${tw`flex flex-col gap-2 p-4 rounded-lg mb-6 border`}
  background-color: #fef2f2;
  border-color: #fecaca;
  color: #991b1b;
`;

const AdvisoryWarning = styled.div`
  ${tw`flex flex-col gap-2 p-4 rounded-lg mb-6 border`}
  background-color: #fffbeb;
  border-color: #fde68a;
  color: #92400e;
`;

const WarningHeaderRow = styled.div`
  ${tw`flex flex-wrap items-center justify-between gap-3`}
`;

const WarningHeading = styled.p`
  ${tw`m-0 text-sm font-semibold leading-snug flex-1 min-w-0`}
`;

const WarningCopy = styled.p`
  ${tw`m-0 text-sm leading-relaxed`}
`;

const EditTripPrefsButton = styled.button`
  ${tw`shrink-0 text-sm font-medium px-3 py-1.5 rounded-md border transition-colors`}
  border-color: #fecaca;
  background-color: #ffffff;
  color: #991b1b;
  &:hover {
    background-color: #fee2e2;
  }
`;

const ToggleRow = styled.div`
  ${tw`flex items-center justify-between gap-4 p-4 border border-border-default rounded-lg mb-6`}
`;

const ToggleText = styled.div`
  ${tw`flex flex-col gap-1`}
`;

const ToggleDescription = styled.p`
  ${tw`text-sm text-text-secondary m-0`}
`;

const ToggleSwitch = styled.input`
  ${tw`h-5 w-5 cursor-pointer`}
`;

const LinkSection = styled.button`
  ${tw`mt-4 w-full text-left border border-border-default rounded-lg p-4 bg-white cursor-pointer transition-colors hover:bg-bg-secondary flex flex-col gap-1`}
`;

const LinkSectionTitle = styled.span`
  ${tw`text-sm font-semibold text-text-primary flex items-center justify-between gap-2`}
`;

const LinkSectionHint = styled.span`
  ${tw`text-xs text-text-secondary`}
`;

const LinkSectionBody = styled.span`
  ${tw`text-sm text-text-secondary leading-snug`}
`;

const StepCard = styled.button`
  ${tw`w-full text-left border border-border-default rounded-lg p-5 bg-white cursor-pointer transition-colors hover:bg-bg-secondary flex flex-col gap-2 mb-4`}
`;

const StepBadge = styled.span`
  ${tw`text-xs font-semibold uppercase tracking-wide text-text-secondary`}
`;

const StepTitle = styled.span`
  ${tw`text-base font-semibold text-text-primary`}
`;

const StepCopy = styled.span`
  ${tw`text-sm text-text-secondary leading-relaxed`}
`;

const LoadingNotice = styled.div`
  ${tw`p-4 rounded-lg border border-border-default text-sm text-text-secondary bg-bg-secondary`}
`;

const shakeHorizontal = keyframes`
  0%,
  100% {
    transform: translateX(0);
  }
  15% {
    transform: translateX(-10px);
  }
  30% {
    transform: translateX(10px);
  }
  45% {
    transform: translateX(-8px);
  }
  60% {
    transform: translateX(8px);
  }
  75% {
    transform: translateX(-4px);
  }
  90% {
    transform: translateX(4px);
  }
`;

const MonthlyInputBlockedNotice = styled.div`
  ${tw`mb-4 p-3 rounded-lg border text-sm border-red-200 bg-red-50 text-red-900`}
`;

const MonthlyInputBlockedNoticeInner = styled.div<{ $shake: boolean }>`
  ${p =>
    p.$shake
      ? css`
          animation: ${shakeHorizontal} 1s ease-in-out;
        `
      : ''}
`;

type Props = {
  carId: string;
  monthlyRentalIsOn?: boolean | null;
  monthlyPrice?: number | null;
  monthlyPrice2Months?: number | null;
  monthlyPrice3Months?: number | null;
  distanceLimit?: DistanceLimit | null;
  maximumRentalDays?: number | null;
};

function numOrNull(v: number | null | undefined): number | null {
  if (v === null || v === undefined || Number.isNaN(Number(v))) return null;
  return Number(v);
}

type TripFeeRow = {
  uniqName: TripFeeUniqNameEnums;
  name: string;
  label: string;
  defaultAmount: number;
};

const MONTHLY_TRIP_FEE_ROWS: TripFeeRow[] = [
  {
    uniqName: TripFeeUniqNameEnums.MonthlyTripfee,
    name: 'Monthly Trip Fee',
    label: 'Trip fee',
    defaultAmount: 0,
  },
  {
    uniqName: TripFeeUniqNameEnums.MonthlyYoungsterfee18,
    name: 'Monthly <21 Youngster Fee',
    label: 'Youngster fee (<21)',
    defaultAmount: 15,
  },
  {
    uniqName: TripFeeUniqNameEnums.MonthlyYoungsterfee21,
    name: 'Monthly 21-25 Youngster Fee',
    label: 'Youngster fee (21–25)',
    defaultAmount: 10,
  },
];

function findMonthlyTripFee(fees: ITripFee[], row: TripFeeRow): ITripFee | undefined {
  return fees.find(f => f.uniqName === row.uniqName || f.name === row.name);
}

function isMonthlyTripFeeRowConfigured(fee: ITripFee | undefined): boolean {
  return !!fee?.id && !!fee.isActive;
}

const MONTHLY_PROTECTION_ORDER: {
  plan: ProtectionPlanEnum;
  label: string;
  fallbackPrice: number;
}[] = [
  { plan: ProtectionPlanEnum.MonthlyMinimum, label: 'Minimum', fallbackPrice: 200 },
  { plan: ProtectionPlanEnum.MonthlyStandard, label: 'Standard', fallbackPrice: 300 },
  { plan: ProtectionPlanEnum.MonthlyPro, label: 'Pro', fallbackPrice: 600 },
  { plan: ProtectionPlanEnum.MonthlyPremium, label: 'Premium', fallbackPrice: 800 },
];

function formatTripFeeAmount(
  amount: number | null | undefined,
  chargeBy: TripFeeTypeEnums | null | undefined,
): string {
  const n = amount ?? 0;
  if (chargeBy === TripFeeTypeEnums.OneTime) return `$${n} one-time`;
  if (chargeBy === TripFeeTypeEnums.Percentage) return `${n}%`;
  return `$${n}/day`;
}

type MonthlyPlanRow = {
  plan: ProtectionPlanEnum;
  dailyPrice?: number | null;
  isActive?: boolean | null;
};

function hasUsableMonthlyProtectionValue(plan: MonthlyPlanRow | undefined): boolean {
  return !!plan && plan.isActive === true;
}

export default function MonthlyPricingSection({
  carId,
  monthlyRentalIsOn: initialMonthlyRentalIsOn,
  monthlyPrice: initialMonthlyPrice,
  monthlyPrice2Months: initialMonthlyPrice2Months,
  monthlyPrice3Months: initialMonthlyPrice3Months,
  distanceLimit,
  maximumRentalDays,
}: Props) {
  const navigate = useNavigate();
  const userId = authStore.user?.id;

  const { data: protectionData, loading: protectionLoading } = useQuery(GET_PROTECTION_PLANS);
  const { data: tripFeesData, loading: tripFeesLoading } = useQuery<
    { tripFees: ITripFee[] },
    { userId: string }
  >(GET_TRIP_FEES, {
    variables: { userId: userId || '' },
    skip: !userId,
    fetchPolicy: 'cache-and-network',
  });

  const monthlyProtectionSummary = useMemo(() => {
    const plans = (protectionData as { protectionPlans?: MonthlyPlanRow[] })?.protectionPlans;
    return MONTHLY_PROTECTION_ORDER.filter(row =>
      hasUsableMonthlyProtectionValue(plans?.find(p => p.plan === row.plan)),
    )
      .map(row => {
        const fromApi = plans?.find(p => p.plan === row.plan);
        const price = fromApi?.dailyPrice ?? row.fallbackPrice;
        return `${row.label} $${Number(price).toFixed(0)}/mo`;
      })
      .join(' · ');
  }, [protectionData]);

  const hasMonthlyProtectionSetup = useMemo(() => {
    const plans = (protectionData as { protectionPlans?: MonthlyPlanRow[] })?.protectionPlans;
    return MONTHLY_PROTECTION_ORDER.some(row =>
      hasUsableMonthlyProtectionValue(plans?.find(p => p.plan === row.plan)),
    );
  }, [protectionData]);

  const hasMonthlyTripFeesSetup = useMemo(() => {
    const fees = tripFeesData?.tripFees ?? [];
    return MONTHLY_TRIP_FEE_ROWS.every(row =>
      isMonthlyTripFeeRowConfigured(findMonthlyTripFee(fees, row)),
    );
  }, [tripFeesData]);

  const monthlyTripFeesSummary = useMemo(() => {
    const fees = tripFeesData?.tripFees ?? [];
    return MONTHLY_TRIP_FEE_ROWS.map(row => {
      const fromApi = findMonthlyTripFee(fees, row);
      const amount = fromApi?.amount ?? row.defaultAmount;
      const chargeBy = fromApi?.chargeBy ?? TripFeeTypeEnums.DailyRate;
      return `${row.label}: ${formatTripFeeAmount(amount, chargeBy)}`;
    }).join(' · ');
  }, [tripFeesData]);

  const [monthlyRentalIsOn, setMonthlyRentalIsOn] = useState<boolean>(
    initialMonthlyRentalIsOn ?? false,
  );
  const [monthlyPrice, setMonthlyPrice] = useState<number | null>(numOrNull(initialMonthlyPrice));
  const [monthlyPrice2Months, setMonthlyPrice2Months] = useState<number | null>(
    numOrNull(initialMonthlyPrice2Months),
  );
  const [monthlyPrice3Months, setMonthlyPrice3Months] = useState<number | null>(
    numOrNull(initialMonthlyPrice3Months),
  );
  // Spec 028: this site's unit.
  const { distanceUnit } = useBrandLocale();
  const [monthlyDistanceLimit, setMonthlyDistanceLimit] = useState<number | null>(
    distanceLimit?.monthlyDistanceLimit ?? defaultMonthlyDistance(distanceUnit),
  );
  const [monthlyUnlimitedMileage, setMonthlyUnlimitedMileage] = useState<boolean>(
    distanceLimit?.monthlyUnlimitedMileage ?? false,
  );

  const distanceKey = useMemo(() => JSON.stringify(distanceLimit ?? null), [distanceLimit]);

  useEffect(() => {
    setMonthlyRentalIsOn(initialMonthlyRentalIsOn ?? false);
  }, [initialMonthlyRentalIsOn]);

  useEffect(() => {
    setMonthlyPrice(numOrNull(initialMonthlyPrice));
    setMonthlyPrice2Months(numOrNull(initialMonthlyPrice2Months));
    setMonthlyPrice3Months(numOrNull(initialMonthlyPrice3Months));
  }, [initialMonthlyPrice, initialMonthlyPrice2Months, initialMonthlyPrice3Months]);

  useEffect(() => {
    setMonthlyDistanceLimit(
      distanceLimit?.monthlyDistanceLimit ?? defaultMonthlyDistance(distanceUnit),
    );
    setMonthlyUnlimitedMileage(distanceLimit?.monthlyUnlimitedMileage ?? false);
  }, [distanceKey]);

  const [updateCar, { loading }] = useMutation(UPDATE_CAR, {
    refetchQueries: [{ query: GET_CAR, variables: { id: carId } }],
  });

  const handleSave = async () => {
    try {
      await updateCar({
        variables: {
          id: carId,
          updateCarInput: {
            monthlyRentalIsOn,
            monthlyPrice,
            monthlyPrice2Months,
            monthlyPrice3Months,
            distanceLimit: {
              dailyDistanceLimit:
                distanceLimit?.dailyDistanceLimit ?? defaultDailyDistance(distanceUnit),
              unlimitedMileage: distanceLimit?.unlimitedMileage ?? false,
              monthlyDistanceLimit: monthlyDistanceLimit ?? defaultMonthlyDistance(distanceUnit),
              monthlyUnlimitedMileage,
            },
          },
        },
      });
      toast.success('Monthly rental settings saved');
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Save failed';
      toast.error(msg);
    }
  };

  const monthlyBlocked =
    maximumRentalDays != null && maximumRentalDays > 0 && maximumRentalDays < 30;

  const monthlySetupLoading = protectionLoading || (!!userId && tripFeesLoading);
  const monthlySetupReady = hasMonthlyProtectionSetup && hasMonthlyTripFeesSetup;
  const showMonthlyEditableFields = monthlySetupReady && monthlyRentalIsOn;
  const monthlyInputsLocked = !showMonthlyEditableFields || monthlyBlocked;

  const [inputBlockedShakeSeq, setInputBlockedShakeSeq] = useState(0);

  const triggerMonthlyInputBlockedShake = () => {
    setInputBlockedShakeSeq(n => n + 1);
  };

  const handleBlockedMonthlyFieldFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    if (!monthlyInputsLocked) {
      const target = e.target;
      const length = target.value.length;
      target.setSelectionRange(length, length);
      return;
    }
    e.target.blur();
    triggerMonthlyInputBlockedShake();
  };

  const blockedMonthlyInputsMessage =
    !monthlyRentalIsOn && monthlyBlocked
      ? 'Turn on monthly rental and set maximum trip duration to 30 days or more before editing these fields.'
      : !monthlyRentalIsOn
        ? 'Turn on monthly rental to edit fixed tier prices and monthly mileage.'
        : 'Set maximum trip duration to 30 days or more in Trip preferences to edit these fields.';

  return (
    <Container>
      <Title>Monthly rental</Title>

      {monthlySetupLoading && <LoadingNotice>Loading monthly setup status...</LoadingNotice>}

      {!monthlySetupLoading && !hasMonthlyProtectionSetup && (
        <StepCard type="button" onClick={() => navigate('/cars/host/protection-plans?tab=monthly')}>
          <StepBadge>Step 1</StepBadge>
          <StepTitle>Set monthly protection plan</StepTitle>
          <StepCopy>
            Add at least one monthly protection value before enabling monthly rental.
          </StepCopy>
        </StepCard>
      )}

      {!monthlySetupLoading && hasMonthlyProtectionSetup && !hasMonthlyTripFeesSetup && (
        <>
          <LinkSection
            type="button"
            onClick={() => navigate('/cars/host/protection-plans?tab=monthly')}
          >
            <LinkSectionTitle>
              Monthly protection plan
              <LinkSectionHint>Edit</LinkSectionHint>
            </LinkSectionTitle>
            <LinkSectionBody>{monthlyProtectionSummary}</LinkSectionBody>
          </LinkSection>

          <StepCard
            type="button"
            onClick={() => navigate('/cars/host/miscellaneous-fees?tab=monthly')}
          >
            <StepBadge>Step 2</StepBadge>
            <StepTitle>Set monthly trip and youngster fees</StepTitle>
            <StepCopy>
              Save monthly trip and youngster fees before enabling monthly rental.
            </StepCopy>
          </StepCard>
        </>
      )}

      {!monthlySetupLoading && monthlySetupReady && (
        <ToggleRow>
          <ToggleText>
            <SectionTitle>Enable monthly rental</SectionTitle>
            <ToggleDescription>
              Monthly bookings use these prices and mileage settings only when enabled.
            </ToggleDescription>
          </ToggleText>
          <ToggleSwitch
            id="monthly-rental-is-on"
            type="checkbox"
            checked={monthlyRentalIsOn}
            onChange={e => setMonthlyRentalIsOn(e.target.checked)}
          />
        </ToggleRow>
      )}

      {!monthlySetupLoading && monthlySetupReady && !monthlyRentalIsOn && (
        <AdvisoryWarning>
          <WarningHeading>Monthly rental is off</WarningHeading>
          <WarningCopy>
            Monthly bookings will not use monthly pricing or monthly mileage until you turn this
            switch on.
          </WarningCopy>
        </AdvisoryWarning>
      )}

      {!monthlySetupLoading && monthlySetupReady && monthlyBlocked && (
        <EligibilityWarning>
          <WarningHeaderRow>
            <WarningHeading>Trip duration blocks monthly rental</WarningHeading>
            <EditTripPrefsButton
              type="button"
              onClick={() =>
                navigate(`/cars/host/view/${encodeURIComponent(carId)}?section=tripPreferences`)
              }
            >
              Edit
            </EditTripPrefsButton>
          </WarningHeaderRow>
          <WarningCopy>
            The maximum trip duration is set to <strong>{maximumRentalDays} days</strong>. Update it
            to 30 days or more in Trip Preferences, and keep the monthly rental switch on, to allow
            monthly bookings.
          </WarningCopy>
        </EligibilityWarning>
      )}

      {showMonthlyEditableFields && (
        <>
          <SectionTitle>Fixed tier prices</SectionTitle>
          <Grid>
            <Field>
              <Label htmlFor="monthly-price-1">30-day price</Label>
              <PriceInput
                id="monthly-price-1"
                placeholder="e.g. 1200"
                value={monthlyPrice}
                onValueChange={setMonthlyPrice}
                minValue={0}
                allowDecimal
                onFocus={handleBlockedMonthlyFieldFocus}
              />
            </Field>
            <Field>
              <Label htmlFor="monthly-price-2">60-day price</Label>
              <PriceInput
                id="monthly-price-2"
                placeholder="e.g. 2200"
                value={monthlyPrice2Months}
                onValueChange={setMonthlyPrice2Months}
                minValue={0}
                allowDecimal
                onFocus={handleBlockedMonthlyFieldFocus}
              />
            </Field>
            <Field>
              <Label htmlFor="monthly-price-3">90-day price</Label>
              <PriceInput
                id="monthly-price-3"
                placeholder="e.g. 3000"
                value={monthlyPrice3Months}
                onValueChange={setMonthlyPrice3Months}
                minValue={0}
                allowDecimal
                onFocus={handleBlockedMonthlyFieldFocus}
              />
            </Field>
          </Grid>

          <SectionTitle>
            {distanceUnit === 'km' ? 'Monthly distance' : 'Monthly mileage'}
          </SectionTitle>
          <Grid>
            <Field>
              <Label htmlFor="monthly-distance">
                {distanceUnit === 'km' ? 'Kilometres per 30-day month' : 'Miles per 30-day month'}
              </Label>
              <PriceInput
                id="monthly-distance"
                placeholder="e.g. 100"
                value={monthlyDistanceLimit}
                onValueChange={setMonthlyDistanceLimit}
                minValue={0}
                onFocus={handleBlockedMonthlyFieldFocus}
              />
            </Field>
            <Field>
              <Label htmlFor="monthly-unlimited">Unlimited monthly distance</Label>
              <input
                id="monthly-unlimited"
                type="checkbox"
                checked={monthlyUnlimitedMileage}
                onChange={e => setMonthlyUnlimitedMileage(e.target.checked)}
                onFocus={e => {
                  if (!monthlyInputsLocked) return;
                  e.target.blur();
                  triggerMonthlyInputBlockedShake();
                }}
              />
            </Field>
          </Grid>
        </>
      )}

      {showMonthlyEditableFields && monthlyInputsLocked && (
        <MonthlyInputBlockedNotice>
          <MonthlyInputBlockedNoticeInner
            key={inputBlockedShakeSeq}
            $shake={inputBlockedShakeSeq > 0}
          >
            {blockedMonthlyInputsMessage}
          </MonthlyInputBlockedNoticeInner>
        </MonthlyInputBlockedNotice>
      )}

      {!monthlySetupLoading && monthlySetupReady && (
        <Button
          buttonWidth="200px"
          text="Save"
          isLoading={loading}
          isDisabled={loading || monthlyBlocked}
          onClick={handleSave}
        />
      )}

      {!monthlySetupLoading && monthlySetupReady && (
        <>
          <LinkSection
            type="button"
            onClick={() => navigate('/cars/host/protection-plans?tab=monthly')}
          >
            <LinkSectionTitle>
              Monthly protection plan
              <LinkSectionHint>Edit</LinkSectionHint>
            </LinkSectionTitle>
            <LinkSectionBody>{monthlyProtectionSummary}</LinkSectionBody>
            <LinkSectionHint>Tap to edit protection plans for monthly rentals.</LinkSectionHint>
          </LinkSection>

          <LinkSection
            type="button"
            onClick={() => navigate('/cars/host/miscellaneous-fees?tab=monthly')}
          >
            <LinkSectionTitle>
              Monthly trip and youngster fees
              <LinkSectionHint>Edit</LinkSectionHint>
            </LinkSectionTitle>
            <LinkSectionBody>{monthlyTripFeesSummary}</LinkSectionBody>
            <LinkSectionHint>Tap to edit trip fees for monthly rentals.</LinkSectionHint>
          </LinkSection>
        </>
      )}
    </Container>
  );
}
